/**
 * CardReviewModal
 *
 * Appears after the EnvelopeAnimation completes. Shows the user:
 *   • The card front preview
 *   • The card message (editable)
 *   • Contact name, occasion type, and send date
 *   • "Looks great" and "Edit message" actions
 *
 * Saving an edited message calls PUT /api/occasions/:id to persist it.
 */

import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../../context/AuthContext';

const MODAL_KEYFRAMES = `
@keyframes overlayFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes textFadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes sealStamp {
  0%   { opacity: 0; transform: rotate(-6deg) scale(0.2); }
  60%  { opacity: 1; transform: rotate(-6deg) scale(1.12); }
  80%  { transform: rotate(-6deg) scale(0.95); }
  100% { transform: rotate(-6deg) scale(1); }
}
@keyframes sealTextFade {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
import CardDesignPreview, { type DesignData } from './CardDesignPreview';
import { apiFetch } from '../../lib/api';
import { deriveGreeting } from '../../lib/greeting';

const CHARS_PER_PRINT_LINE = 45;
const MAX_PRINT_LINES      = 14;
const MAX_CHARS            = 650;

function countPrintLines(text: string): number {
  if (!text) return 0;
  return text.split('\n').reduce((total, line) => {
    return total + (line.length === 0 ? 1 : Math.ceil(line.length / CHARS_PER_PRINT_LINE));
  }, 0);
}

export interface ReviewOccasion {
  id:                   string;
  occasion_type:        string;
  custom_occasion_name: string | null;
  next_send_date:       string | null;
  current_message:      string | null;
  card_design_id:       string | null;
  contact_id:           string | null;
  contact_name:         string;
  relationship_type?:   string | null;
  card_greeting?:       string | null;
  card_type?:           string | null;   // 'standard' | 'handwritten'
}

interface CardReviewModalProps {
  occasion:      ReviewOccasion;
  design:        DesignData | null;
  onClose:       () => void;
  cardClosing?:  string;
  cardFromLine?: string;
  handwritten?:  boolean;
}

const OCCASION_LABELS: Record<string, string> = {
  birthday:        'Birthday',
  anniversary:     'Anniversary',
  christmas:       'Christmas',
  valentines:      "Valentine's Day",
  mothers_day:     "Mother's Day",
  fathers_day:     "Father's Day",
  graduation:      'Graduation',
  thinking_of_you: 'Thinking of You',
  custom:          'Special Day',
};

export default function CardReviewModal({
  occasion,
  design,
  onClose,
  cardClosing  = 'With love,',
  cardFromLine = '',
  handwritten:  handwrittenProp = false,
}: CardReviewModalProps) {
  const { profile } = useAuth();
  const [editing,     setEditing  ]   = useState(false);
  const [message,     setMessage  ]   = useState(occasion.current_message ?? '');
  const [greeting,    setGreeting ]   = useState(
    occasion.card_greeting ??
    deriveGreeting(occasion.relationship_type, occasion.occasion_type, occasion.contact_name)
  );
  // Handwritten is retired. The flag is forced off (the occasions.handwritten
  // data is neutralized in migration 015); kept as a const so the preview's
  // style branches resolve to the standard printed card.
  const [handwritten] = useState(handwrittenProp);
  const [saving,      setSaving   ]   = useState(false);
  const [saved,       setSaved    ]   = useState(false);
  const [saveError,   setSaveError]   = useState('');
  const [confirmed,   setConfirmed]   = useState(false);
  const [localClosing,  setLocalClosing ] = useState(cardClosing);
  const [localFromLine, setLocalFromLine] = useState(cardFromLine);
  const msgFont = handwritten ? '"Great Vibes", cursive' : 'Georgia, serif';

  const occasionLabel = occasion.custom_occasion_name
    ?? OCCASION_LABELS[occasion.occasion_type]
    ?? occasion.occasion_type;

  const sendDate = occasion.next_send_date
    ? new Date(occasion.next_send_date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'long',
        day:   'numeric',
        year:  'numeric',
      })
    : 'Soon';

  async function saveMessage() {
    if (!message.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      await apiFetch(`/api/occasions/${occasion.id}`, {
        method: 'PUT',
        body:   JSON.stringify({ current_message: message, card_greeting: greeting }),
      });
      if (localClosing !== cardClosing || localFromLine !== cardFromLine) {
        await apiFetch('/api/auth/profile', {
          method: 'PATCH',
          body:   JSON.stringify({ cardClosing: localClosing, cardFromLine: localFromLine }),
        });
      }
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save, please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleLooksGreat() {
    setConfirmed(true);
    setTimeout(onClose, 900);
  }

  const overlayStyle: CSSProperties = {
    position:        'fixed',
    inset:           0,
    zIndex:          50,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         16,
    backgroundColor: 'rgba(26,20,16, 0.65)',
    backdropFilter:  'blur(8px)',
    animation:       'overlayFadeIn 0.3s ease both',
  };

  if (confirmed) {
    return (
      <div style={overlayStyle}>
        <style>{MODAL_KEYFRAMES}</style>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius:    24,
          padding:         '48px 32px',
          textAlign:       'center',
          maxWidth:        360,
          width:           '100%',
        }}>
          <img
            src="/logo-icon.png"
            alt="MomentSent seal"
            style={{
              width:     120,
              height:    120,
              marginBottom: 20,
              animation: 'sealStamp 0.55s cubic-bezier(0.22,1,0.36,1) both',
              display:   'block',
              margin:    '0 auto 20px',
            }}
          />
          <p style={{
            fontFamily:   'Georgia, serif',
            fontSize:     22,
            fontWeight:   700,
            color:        '#1A1410',
            marginBottom: 8,
            animation:    'sealTextFade 0.4s ease 0.45s both',
          }}>
            Card sealed &amp; sent! 🎉
          </p>
          <p style={{
            fontFamily: '"Inter", Arial, sans-serif',
            fontSize:   14,
            color:      '#9CA3AF',
            animation:  'sealTextFade 0.4s ease 0.55s both',
          }}>
            We'll print and mail it to {occasion.contact_name} on {sendDate}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <style>{MODAL_KEYFRAMES}</style>
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius:    24,
          boxShadow:       '0 25px 80px rgba(0,0,0,0.25)',
          width:           '100%',
          maxWidth:        440,
          maxHeight:       '90vh',
          overflowY:       'auto',
          display:         'flex',
          flexDirection:   'column',
          gap:             20,
          padding:         24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <p style={{
              fontFamily:  'Georgia, serif',
              fontSize:    20,
              fontWeight:  700,
              color:       '#1A1410',
              marginBottom: 4,
            }}>
              Ready to mail ✉
            </p>
            <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 13, color: '#9CA3AF' }}>
              {occasion.contact_name} · {occasionLabel} · {sendDate}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background:  'none',
              border:      'none',
              cursor:      'pointer',
              color:       '#C4B9AC',
              fontSize:    20,
              lineHeight:  1,
              padding:     4,
              flexShrink:  0,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Card front + message side-by-side on wider screens */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Card front */}
          {design && (
            <div style={{ flexShrink: 0 }}>
              <CardDesignPreview design={design} size="md" showName={false} />
            </div>
          )}

          {/* Message panel */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              fontFamily:    '"Inter",Arial,sans-serif',
              fontSize:      11,
              fontWeight:    600,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color:         '#9CA3AF',
              marginBottom:  8,
            }}>
              {editing ? 'Your message' : 'Message'}
            </p>

            {editing ? (
              <>
                {/* Editable greeting */}
                <input
                  type="text"
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  placeholder="Dear [Name],"
                  style={{
                    width:           '100%',
                    borderRadius:    10,
                    border:          '1.5px solid #E8E0D5',
                    backgroundColor: '#FAFAF9',
                    padding:         '7px 12px',
                    fontFamily:      msgFont,
                    fontSize:        13,
                    color:           '#1A1410',
                    outline:         'none',
                    boxSizing:       'border-box',
                    marginBottom:    6,
                  }}
                />
                <textarea
                  value={message}
                  rows={6}
                  spellCheck
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= MAX_CHARS && countPrintLines(val) <= MAX_PRINT_LINES) {
                      setMessage(val);
                    }
                  }}
                  style={{
                    width:         '100%',
                    borderRadius:  12,
                    border:        '1.5px solid #E8E0D5',
                    backgroundColor: '#FAFAF9',
                    padding:       '10px 12px',
                    fontFamily:    'Georgia, serif',
                    fontSize:      13,
                    lineHeight:    1.65,
                    color:         '#1A1410',
                    resize:        'none',
                    outline:       'none',
                    boxSizing:     'border-box',
                  }}
                  autoFocus
                />
                {handwritten && /\p{Extended_Pictographic}/u.test(message) && (
                  <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 11, marginTop: 4,
                    color: '#D97706', background: '#FFFBEB', borderRadius: 8, padding: '6px 10px' }}>
                    ⚠️ Emojis can't be handwritten, they'll be removed before printing. Replace them with words instead.
                  </p>
                )}
                {(() => {
                  const lines   = countPrintLines(message);
                  const warn    = lines > 6 || message.length > 280;
                  const atLimit = lines >= MAX_PRINT_LINES || message.length >= MAX_CHARS;
                  return (
                    <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 10, textAlign: 'right', marginTop: 4,
                      color: atLimit ? '#DC2626' : warn ? '#D97706' : '#C4B9AC' }}>
                      {lines}/{MAX_PRINT_LINES} lines · {message.length}/{MAX_CHARS} chars
                      {atLimit ? ', limit reached' : warn ? ', near limit' : ''}
                    </p>
                  );
                })()}

                {/* Closing phrase */}
                <div style={{ marginTop: 10 }}>
                  <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 11, fontWeight: 600,
                    color: '#9CA3AF', letterSpacing: '0.06em', textTransform: 'uppercase' as const,
                    marginBottom: 4 }}>
                    Closing phrase
                  </p>
                  <input
                    type="text"
                    value={localClosing}
                    onChange={(e) => setLocalClosing(e.target.value)}
                    placeholder="With love,"
                    maxLength={60}
                    style={{ width: '100%', borderRadius: 10, border: '1.5px solid #E8E0D5',
                      backgroundColor: '#FAFAF9', padding: '7px 12px', fontFamily: 'Georgia, serif',
                      fontSize: 13, color: '#1A1410', outline: 'none', boxSizing: 'border-box' as const, marginBottom: 6 }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6 }}>
                    {['With love,', 'Love,', 'With respect,', 'Respectfully,', 'Sincerely,',
                      'Warmly,', 'Thinking of you,', 'From,', 'Cheers,', 'Your friend,'].map((chip) => (
                      <button key={chip} type="button" onClick={() => setLocalClosing(chip)}
                        style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 11, padding: '3px 10px',
                          borderRadius: 100, border: '1px solid #E8E0D5', backgroundColor: 'transparent',
                          color: '#9CA3AF', cursor: 'pointer' }}>
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>

                {/* From line */}
                <div style={{ marginTop: 10 }}>
                  <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 11, fontWeight: 600,
                    color: '#9CA3AF', letterSpacing: '0.06em', textTransform: 'uppercase' as const,
                    marginBottom: 4 }}>
                    From
                  </p>
                  <input
                    type="text"
                    value={localFromLine}
                    onChange={(e) => setLocalFromLine(e.target.value)}
                    placeholder="Your name"
                    maxLength={80}
                    style={{ width: '100%', borderRadius: 10, border: '1.5px solid #E8E0D5',
                      backgroundColor: '#FAFAF9', padding: '7px 12px', fontFamily: 'Georgia, serif',
                      fontSize: 13, color: '#1A1410', outline: 'none', boxSizing: 'border-box' as const }}
                  />
                </div>
              </>
            ) : (
              <div style={{
                backgroundColor: handwritten ? '#FFFFFF' : '#F8F5EE',
                borderRadius:    12,
                border:          handwritten ? '1.5px dashed #CBD5E1' : 'none',
                padding:         '12px 14px',
                fontFamily:      msgFont,
                fontSize:        handwritten ? 15 : 13,
                lineHeight:      handwritten ? 1.9 : 1.7,
                color:           handwritten ? '#1D4ED8' : '#1A1410',
                whiteSpace:      'pre-wrap',
                wordBreak:       'break-word',
              }}>
                {greeting && (
                  <div style={{ marginBottom: 4 }}>{greeting}</div>
                )}
                {message || 'No message yet, tap "Edit message" below to write one.'}
              </div>
            )}

            {handwritten && !editing && (
              <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 10,
                color: design?.accent_color ?? '#E8C12E', marginTop: 4, opacity: 0.9 }}>
                ✍️ Written in genuine cursive, real ink, real paper
              </p>
            )}

            {/* Closing + signature (digital or typed — never both) */}
            {(localClosing || localFromLine) && (
              <div style={{
                marginTop:    8,
                paddingLeft:  2,
                fontFamily:   msgFont,
                fontSize:     handwritten ? 13 : 12,
                lineHeight:   1.6,
                color:        handwritten ? '#1D4ED8' : '#6B7280',
                fontStyle:    'italic',
              }}>
                {localClosing && <div>{localClosing}</div>}
                {localFromLine && <div>{localFromLine}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Mailing info strip */}
        <div style={{
          backgroundColor: design ? `${design.accent_color}12` : '#F5F5F5',
          borderRadius:    12,
          padding:         '10px 14px',
          display:         'flex',
          alignItems:      'center',
          gap:             10,
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>📬</span>
          <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 12, color: '#6B7280', margin: 0 }}>
            {handwritten ? 'Written in genuine cursive on' : 'Printed on'}{' '}
            <strong style={{ color: '#1A1410' }}>premium cardstock</strong> and
            mailed directly to {occasion.contact_name} by {sendDate}.
          </p>
        </div>

        {/* Actions */}
        {saved && (
          <p style={{ textAlign: 'center', fontFamily: '"Inter",Arial,sans-serif', fontSize: 13, color: '#E8C12E' }}>
            ✓ Message saved
          </p>
        )}
        {saveError && (
          <p style={{ textAlign: 'center', fontFamily: '"Inter",Arial,sans-serif', fontSize: 13, color: '#DC2626' }}>
            ⚠ {saveError}
          </p>
        )}

        {/* Allowance / overage notice so a scheduled send is never a surprise charge */}
        {(() => {
          const bal = profile?.card_allowance_balance ?? 0;
          return bal > 0 ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#F0F5F0', border: '1px solid #D6E5D8', borderRadius: 12, padding: '10px 14px' }}>
              <span style={{ fontSize: 14, lineHeight: 1 }}>✓</span>
              <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 12, color: '#374151', lineHeight: 1.5, margin: 0 }}>
                This uses <strong style={{ color: '#1A1410' }}>1 of your {bal} included {bal === 1 ? 'card' : 'cards'}</strong> left this period. No extra charge.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '10px 14px' }}>
              <span style={{ fontSize: 14, lineHeight: 1 }}>💳</span>
              <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 12, color: '#92400E', lineHeight: 1.5, margin: 0 }}>
                You've used your included cards for this period, so this card will be billed at the flat <strong>$5.99 overage</strong> rate on your next invoice.
              </p>
            </div>
          );
        })()}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={handleLooksGreat}
            style={{
              width:           '100%',
              padding:         '14px 0',
              borderRadius:    100,
              border:          'none',
              backgroundColor: '#1A1410',
              color:           '#FFFFFF',
              fontFamily:      '"Inter",Arial,sans-serif',
              fontSize:        15,
              fontWeight:      600,
              cursor:          'pointer',
              transition:      'background-color 0.2s',
            }}
          >
            {(profile?.card_allowance_balance ?? 0) > 0 ? 'Looks great, send it! 🚀' : 'Send and pay $5.99 🚀'}
          </button>

          {editing ? (
            <button
              onClick={saveMessage}
              disabled={saving}
              style={{
                width:           '100%',
                padding:         '12px 0',
                borderRadius:    100,
                border:          '1.5px solid #E8C12E',
                backgroundColor: 'transparent',
                color:           '#E8C12E',
                fontFamily:      '"Inter",Arial,sans-serif',
                fontSize:        14,
                fontWeight:      600,
                cursor:          saving ? 'default' : 'pointer',
                opacity:         saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save message'}
            </button>
          ) : (
            <button
              onClick={() => setEditing(true)}
              style={{
                width:           '100%',
                padding:         '12px 0',
                borderRadius:    100,
                border:          '1.5px solid #E8E0D5',
                backgroundColor: 'transparent',
                color:           '#6B7280',
                fontFamily:      '"Inter",Arial,sans-serif',
                fontSize:        14,
                cursor:          'pointer',
              }}
            >
              ✏️ Edit message
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
